import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Youtube } from "lucide-react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useGenerateQuestionsYouTube } from "@/features/questions/api/use-generate-questions-youtube";
import { useCreateStudyset } from "@/features/studysets/api/use-create-studyset";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import { useFreeUploadLimit } from "@/lib/hooks/use-free-upload-limit";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { youtubeValidator } from "@/lib/types";
import { useI18n } from "@/locales/client";

interface PasteTextFormProps {
  id?: string;
  cancel: () => void;
  cancelText: string;
  handleComplete: () => void;
}

const schema = youtubeValidator;

export const YouTubeUrlForm = ({
  id,
  cancel,
  cancelText,
  handleComplete,
}: PasteTextFormProps) => {
  const { data, status } = useUserPlanQuery();
  const { onOpen: onOpenPricing } = usePricingModal();
  const t = useI18n();
  const freeUploadLimit = useFreeUploadLimit();

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      url: "",
    },
    mode: "onChange",
  });
  const createStudySetMutation = useCreateStudyset();
  const generateQuestionMutation = useGenerateQuestionsYouTube();
  const router = useRouter();

  if (status === "pending") {
    return (
      <div className="flex flex-col items-center justify-center pt-5 pb-6">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="animate-spin text-blue-500 size-5" />
          <p className="text-sm text-[#09090B]">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <p className="text-sm font-medium text-destructive">
        {t("newStudySetDrawer.errors.generic")}
      </p>
    );
  }

  const hasReachedLimit =
    !data.isSubscribed && data.generationCount >= freeUploadLimit;

  async function onSubmit(values: z.infer<typeof schema>) {
    if (hasReachedLimit) {
      onOpenPricing({
        upgradeReason: t("newStudySetDrawer.upgradeReason"),
        description: t("newStudySetDrawer.upgradeDescription"),
        paywallType: PAYWALL_TYPES.YOUTUBE_URL,
      });
      posthog.capture("paywall_shown", {
        paywallType: PAYWALL_TYPES.YOUTUBE_URL,
        userId: data?.userId,
      });
      cancel();
      return;
    }

    try {
      let studySetId = id;

      if (!studySetId) {
        const newStudySet = await createStudySetMutation.mutateAsync({
          name: t("newStudySetDrawer.untitledStudySet"),
        });
        studySetId = newStudySet.id;
        posthog.capture("study_set_created", {
          studySetId: newStudySet.id,
          userId: newStudySet.userId,
          creationMethod: "youtube_url",
        });
      }

      await generateQuestionMutation.mutateAsync({
        id: studySetId,
        url: values.url,
      });

      if (!id) {
        router.push(`/studysets/${studySetId}/loading`);
      }
      handleComplete();
    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error(t("newStudySetDrawer.errors.generationFailed"));
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="relative">
                  <Input
                    className="pl-10"
                    disabled={generateQuestionMutation.isPending}
                    placeholder={t("newStudySetDrawer.youtubeUrlPlaceholder")}
                    {...field}
                  />
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Youtube className="size-4 text-muted-foreground" />
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col-reverse gap-y-4 md:gap-y-0 md:flex-row w-full md:items-center md:justify-between">
          <Button
            disabled={generateQuestionMutation.isPending}
            onClick={cancel}
            type="button"
            variant="ghost"
          >
            {cancelText}
          </Button>

          <Button disabled={generateQuestionMutation.isPending} type="submit">
            {generateQuestionMutation.isPending ? (
              <div className="flex items-center gap-2">
                <Loader2 className="animate-spin size-4" />{" "}
                {cancelText !== "Go Back"
                  ? t("newStudySetDrawer.generatingQuestions")
                  : t("newStudySetDrawer.generatingStudySet")}
              </div>
            ) : (
              <span>
                {cancelText !== "Go Back"
                  ? t("newStudySetDrawer.generateQuestions")
                  : t("newStudySetDrawer.generateStudySet")}
              </span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
